import React, { useState } from 'react';

import {
  FormControl,
  FormLabel,
  Input,
  PasswordInput,
  Select,
  VStack,
} from '../../core-ui';
import { useFormContext } from '../../core-ui/ModalForm';
import { objectMap } from '../../support/objectMap';
import { DEFAULT_USER_PERMISSIONS } from '../../support/shared/constants/UserConstants';
import { UserPermissions as Permissions } from '../../types/shared/User';
import UserPermissions from './UserPermissions';

const FULL_PERMISSIONS: Permissions = objectMap(
  DEFAULT_USER_PERMISSIONS,
  () => 'WRITE',
);

type User = {
  name: string;
  email: string;
  username: string;
  password?: string;
  permissions: Permissions;
  suspended: boolean;
};

type Props = {
  user: User | null;
  onSave: (user: User) => void;
  passwordPlaceholder?: string;
};

export function UserEditForm(props: Props) {
  const { user, onSave, passwordPlaceholder } = props;
  const [userFullName, setUserFullName] = useState(user?.name ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [userPassword, setUserPassword] = useState(user?.password ?? '');
  const [userEmail, setUserEmail] = useState(user?.email ?? '');
  const [userSuspended, setUserSuspended] = useState(user?.suspended ?? false);
  const [userPermissions, setUserPermissions] = useState<Permissions>(
    toPermissions(user?.permissions ?? {}),
  );
  const [showCustomPermissions, setShowCustomPermissions] = useState(() => {
    return Object.keys(FULL_PERMISSIONS).some(
      (key) => userPermissions[key] !== 'WRITE',
    );
  });

  const formContext = useFormContext();
  formContext.onSubmit(() => {
    onSave({
      name: userFullName,
      username: username,
      password: userPassword,
      email: userEmail,
      suspended: userSuspended,
      permissions: userPermissions,
    });
  });
  return (
    <VStack align="stretch">
      <FormControl>
        <FormLabel>{t('Full Name')}</FormLabel>
        <Input
          autoFocus={true}
          onChange={(event) => setUserFullName(event.target.value)}
          value={userFullName}
        />
      </FormControl>
      <FormControl>
        <FormLabel>{t('Username')}</FormLabel>
        <Input
          onChange={(event) => setUsername(event.target.value)}
          value={username}
        />
      </FormControl>
      <FormControl>
        <FormLabel>{t('Password')}</FormLabel>
        <PasswordInput
          placeholder={passwordPlaceholder}
          onChange={(event) => setUserPassword(event.target.value)}
          value={userPassword}
        />
      </FormControl>
      <FormControl>
        <FormLabel>{t('Email')}</FormLabel>
        <Input
          onChange={(event) => setUserEmail(event.target.value)}
          value={userEmail}
        />
      </FormControl>
      <FormControl>
        <FormLabel>{t('Suspended')}</FormLabel>
        <Select
          options={[true, false]}
          value={userSuspended}
          getLabel={(value) => (value ? t('Yes') : t('No'))}
          onChange={(value) => setUserSuspended(value)}
        />
      </FormControl>
      <FormControl>
        <FormLabel>{t('Permissions')}</FormLabel>
        <Select
          options={[false, true]}
          value={showCustomPermissions}
          getLabel={(showCustom) =>
            showCustom ? t('Custom permissions') : t('Full permissions')
          }
          onChange={(showCustom) => {
            setUserPermissions(FULL_PERMISSIONS);
            setShowCustomPermissions(showCustom);
          }}
        />
      </FormControl>
      {showCustomPermissions ? (
        <UserPermissions
          permissions={userPermissions}
          onChange={(permissions) => setUserPermissions(permissions)}
        />
      ) : null}
    </VStack>
  );
}

/**
 * By default, user.permissions might have additional keys such as __typename
 */
function toPermissions(permissions: Partial<Permissions>) {
  const result: Partial<Permissions> = {};
  for (const key of Object.keys(FULL_PERMISSIONS)) {
    result[key] = permissions[key] ?? 'WRITE';
  }
  return result as Permissions;
}
